import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, Tabs, Drawer, Spin, Badge, Typography, Empty, Timeline, message
} from 'antd';
import {
  GlobalOutlined, TeamOutlined, ThunderboltOutlined, EyeOutlined
} from '@ant-design/icons';
import { analyticsAPI, connectLiveSocket } from '../api/analytics';
import GoogleInsightsPanel from './GoogleInsightsPanel';

const { Text } = Typography;

// نگاشت کد کانال (که سرور محاسبه می‌کند) به برچسب فارسیِ قابل‌فهم برای ادمین.
const CHANNEL_LABELS = {
  sms_invite: { label: 'دعوت پیامکی', color: 'purple' },
  referral: { label: 'دعوت دوستان', color: 'gold' },
  google_organic: { label: 'جستجوی گوگل', color: 'blue' },
  referral_site: { label: 'سایت دیگر', color: 'cyan' },
  direct: { label: 'مستقیم', color: 'default' },
  unknown: { label: 'نامشخص', color: 'default' }
};

function channelTag(channel, detail) {
  if (channel?.startsWith('utm:')) {
    return <Tag color="green">{`کمپین: ${channel.slice(4)}`}</Tag>;
  }
  const info = CHANNEL_LABELS[channel] || { label: channel || 'نامشخص', color: 'default' };
  return (
    <Tag color={info.color}>
      {info.label}
      {detail ? ` — ${detail}` : ''}
    </Tag>
  );
}

function userLabel(user, isAdmin) {
  if (isAdmin) return <Tag color="gold">مدیر / استاد</Tag>;
  if (!user) return <Text type="secondary">مهمان (بدون ورود)</Text>;
  const name = (user.farsiFirstName && user.farsiLastName)
    ? `${user.farsiFirstName} ${user.farsiLastName}`
    : `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return <span>{name || user.username || user.phoneNumber}</span>;
}

export default function AnalyticsManager() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [drawerUser, setDrawerUser] = useState(null); // { userId, label }
  const [timeline, setTimeline] = useState(null);
  
  const [completeEvents, setCompleteEvents] = useState([]);
  const [completeTotal, setCompleteTotal] = useState(0);
  const [completePage, setCompletePage] = useState(1);
  const [completeLoading, setCompleteLoading] = useState(false);
  
  const socketRef = useRef(null);

  const loadAll = async () => {
    try {
      const [ov, pages, sess] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getTopPages(),
        analyticsAPI.getRecentSessions(50)
      ]);
      if (ov.data.success) setOverview(ov.data);
      if (pages.data.success) setTopPages(pages.data.pages);
      if (sess.data.success) setSessions(sess.data.sessions);
      
      loadCompleteEvents(1);
    } catch {
      message.error('خطا در بارگذاری آمار بازدید');
    } finally {
      setLoading(false);
    }
  };

  const loadCompleteEvents = async (page) => {
    setCompleteLoading(true);
    try {
      const res = await analyticsAPI.getCompleteEvents(page, 50);
      if (res.data.success) {
        setCompleteEvents(res.data.events);
        setCompleteTotal(res.data.total);
        setCompletePage(res.data.page);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCompleteLoading(false);
    }
  };

// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAll();
    const poll = setInterval(loadAll, 30000); // بازدهی کلی هر ۳۰ ثانیه رفرش می‌شود

    const socket = connectLiveSocket();
    socketRef.current = socket;
    socket.on('visit_event', (evt) => {
      setLiveEvents((prev) => [evt, ...prev].slice(0, 100));
    });
    socket.on('presence_snapshot', (online) => {
      setOnlineCount(online.length);
    });
    socket.on('connect_error', () => {
      // بی‌صدا — اگر توکن نامعتبر باشد کاربر همچنان بقیه‌ی پنل را می‌بیند،
      // فقط زنده‌بودن کار نمی‌کند و آمار تجمعی (polling) همچنان جاری است.
    });

    return () => {
      clearInterval(poll);
      socket.disconnect();
    };
  }, []);

  const openUserTimeline = async (userId, label) => {
    setDrawerUser({ userId, label });
    setTimeline(null);
    try {
      const res = await analyticsAPI.getUserTimeline(userId);
      if (res.data.success) setTimeline(res.data);
    } catch {
      message.error('خطا در بارگذاری تاریخچه کاربر');
    }
  };

  const sessionColumns = useMemo(() => [
    {
      title: 'کاربر',
      dataIndex: 'user',
      render: (user, row) => (
        user ? (
          <a onClick={() => openUserTimeline(user._id, userLabel(user, row.isAdmin))}>{userLabel(user, row.isAdmin)}</a>
        ) : userLabel(null, row.isAdmin)
      )
    },
    { title: 'منبع ورود', dataIndex: 'channel', render: (c, row) => channelTag(c, row.channelDetail) },
    { title: 'آخرین صفحه', dataIndex: 'lastPath' },
    { title: 'دستگاه', dataIndex: 'device' },
    { title: 'مرورگر', dataIndex: 'browser' },
    { title: 'تعداد رویداد', dataIndex: 'eventCount' },
    {
      title: 'آخرین فعالیت',
      dataIndex: 'lastEventAt',
      render: (d) => new Date(d).toLocaleString('fa-IR')
    }
  ], []);

  const liveColumns = [
    { title: 'زمان', dataIndex: 'createdAt', render: (d) => new Date(d).toLocaleTimeString('fa-IR') },
    { title: 'نوع', dataIndex: 'type', render: (t, row) => t === 'pageview' ? <Tag icon={<EyeOutlined />}>بازدید صفحه</Tag> : <Tag color="volcano" icon={<ThunderboltOutlined />}>{row.action || 'اکشن'}</Tag> },
    { title: 'مسیر', dataIndex: 'path' },
    { title: 'منبع ورود', dataIndex: 'channel', render: (c, row) => channelTag(c, row.channelDetail) },
    { title: 'دستگاه', dataIndex: 'device' },
    { title: 'کاربر', dataIndex: 'userId', render: (id, row) => row.isAdmin ? <Tag color="gold">مدیر</Tag> : (id ? <Tag color="green">کاربر ثبت‌نامی</Tag> : <Tag>مهمان</Tag>) }
  ];

  const channelRows = (list, key) => (list || []).map((r) => ({ ...r, key: r.channel }));

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title={<span><Badge status="processing" /> آنلاین همین الان</span>}
              value={onlineCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic title="بازدیدکننده یکتا (۳۰ روز اخیر)" value={overview?.totals?.uniqueVisitors ?? 0} prefix={<GlobalOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic title="بازدیدکننده‌ی وارد‌شده به حساب" value={overview?.totals?.loggedInVisitors ?? 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic title="مجموع رویدادها (۳۰ روز اخیر)" value={overview?.totals?.events ?? 0} />
          </Card>
        </Col>
      </Row>

      <Tabs
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'live',
            label: 'زنده (لحظه‌ای)',
            children: (
              <Table
                rowKey="id"
                dataSource={liveEvents}
                columns={liveColumns}
                pagination={{ pageSize: 15 }}
                locale={{ emptyText: <Empty description="هنوز رویداد زنده‌ای دریافت نشده — منتظر بازدید بعدی بمانید" /> }}
                size="small"
              />
            )
          },
          {
            key: 'sessions',
            label: 'نشست‌های اخیر',
            children: (
              <Table
                rowKey="sessionId"
                dataSource={sessions}
                columns={sessionColumns}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'complete',
            label: 'تاریخچه کامل',
            children: (
              <Table
                rowKey="_id"
                dataSource={completeEvents}
                columns={[
                  { title: 'زمان', dataIndex: 'createdAt', render: (d) => new Date(d).toLocaleString('fa-IR') },
                  { title: 'نوع', dataIndex: 'type', render: (t, row) => t === 'pageview' ? <Tag icon={<EyeOutlined />}>بازدید صفحه</Tag> : <Tag color="volcano" icon={<ThunderboltOutlined />}>{row.action || 'اکشن'}</Tag> },
                  { title: 'مسیر', dataIndex: 'path' },
                  { title: 'کاربر', dataIndex: 'user', render: (u, row) => userLabel(u, row.isAdmin) },
                  { title: 'منبع ورود', dataIndex: 'channel', render: (c, row) => channelTag(c, row.channelDetail) },
                  { title: 'دستگاه', dataIndex: 'device' },
                  { title: 'آی‌پی', dataIndex: 'ip', render: ip => ip || '-' },
                ]}
                pagination={{
                  current: completePage,
                  pageSize: 50,
                  total: completeTotal,
                  onChange: (page) => loadCompleteEvents(page)
                }}
                loading={completeLoading}
                size="small"
              />
            )
          },
          {
            key: 'google',
            label: 'گوگل',
            children: <GoogleInsightsPanel />
          },
          {
            key: 'sources',
            label: 'منابع ورود',
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Card title="بازدیدها به تفکیک منبع" size="small">
                    <Table
                      rowKey="channel"
                      dataSource={channelRows(overview?.visitsByChannel)}
                      pagination={false}
                      size="small"
                      columns={[
                        { title: 'منبع', dataIndex: 'channel', render: (c) => channelTag(c) },
                        { title: 'بازدیدکننده یکتا', dataIndex: 'uniqueVisitors' },
                        { title: 'تعداد رویداد', dataIndex: 'events' }
                      ]}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="ثبت‌نام‌ها به تفکیک منبع" size="small">
                    <Table
                      rowKey="channel"
                      dataSource={channelRows(overview?.usersByChannel)}
                      pagination={false}
                      size="small"
                      columns={[
                        { title: 'منبع', dataIndex: 'channel', render: (c) => channelTag(c) },
                        { title: 'تعداد کاربر', dataIndex: 'count' }
                      ]}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="بازدیدها به تفکیک پلتفرم" size="small">
                    <Table
                      rowKey="platform"
                      dataSource={(overview?.visitsByPlatform || []).map((r) => ({ ...r, key: r.platform }))}
                      pagination={false}
                      size="small"
                      columns={[
                        { title: 'پلتفرم', dataIndex: 'platform', render: (p) => p === 'android-app' ? <Tag color="red">اپلیکیشن اندروید</Tag> : <Tag color="blue">وب‌سایت</Tag> },
                        { title: 'بازدیدکننده یکتا', dataIndex: 'uniqueVisitors' },
                        { title: 'تعداد رویداد', dataIndex: 'events' }
                      ]}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="پربازدیدترین صفحات" size="small">
                    <Table
                      rowKey="path"
                      dataSource={topPages}
                      pagination={{ pageSize: 10 }}
                      size="small"
                      columns={[
                        { title: 'مسیر', dataIndex: 'path' },
                        { title: 'بازدید', dataIndex: 'views' },
                        { title: 'بازدیدکننده یکتا', dataIndex: 'uniqueVisitors' }
                      ]}
                    />
                  </Card>
                </Col>
              </Row>
            )
          }
        ]}
      />

      <Drawer
        title={drawerUser ? <>تاریخچه‌ی «{drawerUser.label}»</> : ''}
        open={!!drawerUser}
        onClose={() => setDrawerUser(null)}
        width={480}
      >
        {!timeline ? (
          <Spin />
        ) : (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <div>منبع ورود اولیه: {channelTag(timeline.user.acquisitionChannel, timeline.user.acquisitionDetail)}</div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">تاریخ ثبت‌نام: {new Date(timeline.user.createdAt).toLocaleString('fa-IR')}</Text>
              </div>
            </Card>
            <Timeline
              items={(timeline.events || []).map((e) => ({
                children: (
                  <div>
                    <div><Text strong>{e.type === 'pageview' ? `بازدید: ${e.path || ''}` : `اکشن: ${e.action || ''}`}</Text></div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{new Date(e.createdAt).toLocaleString('fa-IR')}</Text>
                  </div>
                )
              }))}
            />
            {timeline.events?.length === 0 && <Empty description="رویدادی ثبت نشده" />}
          </>
        )}
      </Drawer>
    </Spin>
  );
}
